<template>
    <b-card no-body class="card-accent-warning rounded-0 border-left-0 border-right-0 mb-0 bg-gradient-light">
        <validation-observer ref="observer" v-slot="{ passes }">
            <b-form @submit.prevent="passes(onFormSubmit)" novalidate>
            <b-tabs pills card content-class="border-left-0 border-bottom-0 border-right-0" active-nav-item-class="bg-warning">
                <b-tab active>
                    <template slot="title">
                        <i class="fa-sharp fa-solid fa-heart-pulse"></i> Episode <b-spinner v-if="isDirty" variant="light" small label="Small Spinner"></b-spinner>
                    </template>
                    <validation-provider vid="name" v-slot="{ errors }">
                    <b-form-group
                        label="Episode Name"
                        label-for="name"
                        :invalid-feedback="errors[0]"
                        :state="errors[0] ? false : null">
                        <b-input-group>
                            <b-form-input :state="icpc ? true : errors[0] ? false : null" class="squared-bottom" id="name" name="name" v-model="form.name" placeholder="Generic Episode" autocomplete="off" @input="debouncedUpdate" />
                            <b-input-group-append>
                                <b-input-group-text>
                                    {{ toLocaleDate(current?.start) }}
                                </b-input-group-text>
                            </b-input-group-append>
                        </b-input-group>
                        <validation-provider v-if="useICPC" vid="icpc_id" v-slot="{ errors }">
                          <b-button
                            block
                            type="button"
                            class="squared-top"
                            :variant="errors[0] ? 'danger' : (icpc ? 'success' : 'primary')"
                            @click="icpc ? unsetICPC() : showICPCModal()"
                          >
                            <i :class="icpc ? 'fa-solid fa-ban' : 'fa-solid fa-seal'"></i>
                            {{ icpc ? icpc : 'ICPC Episode' }}
                          </b-button>
                        </validation-provider>
                        <validation-provider v-if="useICD10" vid="icd10_id" v-slot="{ errors }">
                            <b-button
                                block
                                type="button"
                                class="squared-top"
                                :variant="errors[0] ? 'danger' : (icd10 ? 'success' : 'primary')"
                                @click="icd10 ? unsetICD10() : showICD10Modal()"
                            >
                                <i :class="icd10 ? 'fa-solid fa-ban' : 'fa-solid fa-seal'"></i>
                                {{ icd10 ? icd10 : 'ICD-10 Episode' }}
                            </b-button>
                        </validation-provider>
                    </b-form-group>
                    </validation-provider>
                    <b-form-group
                        label="Most Recent:">
                        <b-list-group>
                            <template v-if="hasActiveEpisodes">
                            <b-list-group-item href="#" @click="updateDecursusEpisode(episode)" class="p-0 flex-column align-items-start" v-for="episode in activeEpisodes" :key="`episode-${episode.id}`">
                                <div v-if="episode.bookmarked" class="position-relative">
                                    <i style="top:0;left:8px;" class="position-absolute fa-sharp fa-solid fa-bookmark text-warning"></i>
                                </div>
                                <div class="ml-2 p-2">
                                    <div class="d-flex w-100 justify-content-between">
                                        <h6 class="mb-1">{{ episode.name }}</h6>
                                        <small>{{ toLocaleDate(episode.start) }}</small>
                                    </div>

                                    <small v-if="useICPC && episode.icpc">{{ episode.icpc.code }} - {{ episode.icpc.name }}</small>
                                    <small v-if="useICD10 && episode.icd10">{{ episode.icd10.tag === 'subcode' ? `${episode.icd10.parent} + ${episode.icd10.code}` : episode.icd10.code }} - {{ episode.icd10.name }}</small>
                                </div>
                            </b-list-group-item>
                            </template>
                            <template>
                                <b-list-group-item href="#" @click="showEpisodeModal">Show more</b-list-group-item>
                            </template>
                        </b-list-group>
                    </b-form-group>
                </b-tab>
            </b-tabs>
            </b-form>
        </validation-observer>
    </b-card>
</template>
<script>
import Vue from 'vue'
import FormExtension from "@/core/components/FormExtension"
import { mapGetters, mapActions } from 'vuex'

import EpisodeModal from './EpisodeModal'
import ICPCModal from '../catalogue/ICPCModal'
import ICD10Modal from '../catalogue/ICD10Modal'
import { useICPC, useICD10 } from '@/shared/clinicCodes'

import debounce from 'lodash/debounce'

export default FormExtension.extend({
    name: 'decursus-episode',
    props: ['limit'],
    data() {
        return {
            useICPC,
            useICD10,
            form: new Form({
                name: "",
                icpc_id: null,
                icpc: null,
                icd10_id: null,
                icd10: null,
                consistency_token: null
            }),

            confirm: false,

            debouncedUpdate: null
        }
    },
    computed: {
        ...mapGetters('episodes', ['getCurrentEpisode', 'getEpisodes', 'getActiveEpisodes']),
        current() {
            return this.getCurrentEpisode()
        },
        ...mapGetters('decursus', ['getCurrentDecursus']),
        decursus() {
            return this.getCurrentDecursus()
        },
        episodes() {
            return this.getEpisodes()
        },
        activeEpisodes() {
            if (!this.current)
                return []
            
            return this.getActiveEpisodes().filter(episode => episode.id !== this.current?.id).slice(0, this.limit)
        },
        hasActiveEpisodes() {
            return this.activeEpisodes.length > 0
        },
        hasEpisodes() {
            return this.episodes.length > 0
        },
        icpc() {
            return this.form.icpc_id ? `${this.form.icpc.code} ${this.form.icpc.name}` : null
        },
        icd10() {
            if (!this.form.icd10_id) return null
            const code = this.form.icd10.tag === 'subcode' ? `${this.form.icd10.parent} + ${this.form.icd10.code}` : this.form.icd10.code
            return `${code} - ${this.form.icd10.name}`
        }
    },
    watch: {
        icpc(val) {
            if (this.form.dirty()) {
                this.debouncedUpdate()
            }
        },
        icd10(val) {
            if (this.form.dirty()) {
                this.debouncedUpdate()
            }
        },

        current: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.form.withData(val)
                }
            }
        },
        form: {
            deep: true,
            handler(val) {
                this.isDirty = this.form.dirty()

                if (this.isDirty) {
                    this.dirty()
                }else{
                    this.validated()
                }
            }
        }
    },
    methods: {
        ...mapActions('decursus', ['updateDecursus']),
        ...mapActions('episodes', ['getEpisodeById', 'getEpisodeForCurrentDecursus', 'getEpisodesForCurrentPatient', 'updateEpisode', 'setEpisode']),
        async loadForDecursus(decursus) {
            await this.getEpisodeForCurrentDecursus()
        },

        async loadForPatient(patient) {
            await this.getEpisodesForCurrentPatient()
        },
        onFormSubmit() {
            this.confirm = true
        },
        onUpdate(value=null, notify=false) {
            this.updateEpisode(this.form.data()).then(() => {
                if (notify) {
                    this.notify("Episode updated", "Patient episode was updated successfully")
                }

                this.confirm = false
            })
            .catch(errors => {
                if (errors.response) {
                    if (notify) {
                        this.notify("Error", "Something went wrong", 'danger')
                    }

                    this.$refs.observer?.setErrors(errors.response.data)
                    this.hasErrors = true
                    this.confirm = false
                }
            })
        },
        onConfirmed() {
            this.onUpdate(null, true)
        },
        onShown() {
            // Focus the dialog prompt
            this.$refs.dialog.focus()
        },
        onCancel() {
            this.confirm = false
        },
        showICPCModal() {
            var ComponentClass = Vue.extend(ICPCModal)
            var instance = new ComponentClass({ parent: this })

            instance.$on("hidden", () => {
                instance.$destroy()
            })

            instance.$on("selected", (icpc) => {
                this.form.populate({icpc_id: icpc.id})
                this.form.populate({icpc: icpc})
            })

            instance.$mount()
        },

        unsetICPC() {
            this.form.populate({icpc: null})
            this.form.populate({icpc_id: null})
        },

        showICD10Modal() {
            var ComponentClass = Vue.extend(ICD10Modal)
            var instance = new ComponentClass({ parent: this })

            instance.$on("hidden", () => {
                instance.$destroy()
            })

            instance.$on("selected", (icd10) => {
                this.form.populate({icd10_id: icd10.id})
                this.form.populate({icd10: icd10})
            })

            instance.$mount()
        },

        unsetICD10() {
            this.form.populate({icd10: null})
            this.form.populate({icd10_id: null})
        },

        async updateDecursusEpisode(episode) {
            this.isDirty = true
            try {
                await this.updateDecursus({ notify: false, data: { episode: episode.id, consistency_token: this.decursus.consistency_token} })
            } catch (e) {
                console.error('Error updating decursuse pisode:', e)
            } finally {
                this.isDirty = false
            }
        },

        showEpisodeModal() {
            var ComponentClass = Vue.extend(EpisodeModal)
            var instance = new ComponentClass({ parent: this })

            instance.$on("hidden", () => {
                instance.$destroy()
            })

            instance.$on("selected", (episode) => {
                this.updateDecursusEpisode(episode)
            })

            instance.$mount()
        },
        reset() {
            this.form.reset()
            // this.setEpisode(this.current)

            this.$nextTick(() => {
                this.$refs.observer?.reset()
                this.hasErrors = false
            })
        },
    },
    created() {
        // Initialize the debounced function in the correct context
        this.debouncedUpdate = debounce(this.onUpdate, 1000)
    },
    beforeDestroy() {
        this.debouncedUpdate.cancel()
    }
})
</script>